/**
 * What the phone knows about a failed payment that the server cannot.
 *
 * The API reports its own faults, and Stripe's webhook reports every payment it
 * refused. Neither of them can see three things:
 *
 *   - somebody closing the payment sheet, which is not a failure at all;
 *   - the sheet refusing to open in the first place, which never becomes a
 *     Stripe payment and so never becomes a webhook;
 *   - THE SHEET SAYING THE PAYMENT WENT THROUGH AND OUR CONFIRMATION FAILING
 *     AFTER IT. That is the one that matters most: the money has moved and the
 *     person is not registered for the activity they just paid for.
 *
 * The vocabulary is deliberately the same as the API's, so that an alert reads
 * the same whichever project raised it.
 */
export type WhatWentWrongWithThePayment =
  /** The person closed the sheet. Nothing is broken, and nobody is told. */
  | 'abandoned'
  /** The card said no. Counted, never raised. */
  | 'refused-by-the-bank'
  /** Ours. The default, because an unnamed failure is ours until proven otherwise. */
  | 'ours-to-fix'
  /** The sheet said it was paid and we could not confirm it. The worst case. */
  | 'paid-but-not-confirmed';

/**
 * What the Stripe sheet answered, as a value rather than as a sentence.
 *
 * The adapter used to flatten all of this into free text: 'Payment was
 * canceled' and 'Payment failed' both arrived as a string, and the state could
 * only tell them apart by reading English prose. That is why an abandoned
 * basket and a broken sheet were indistinguishable.
 */
export type WhatTheSheetSaid =
  | 'completed'
  | 'cancelled'
  | 'failed'
  | 'would-not-open'
  | 'something-else';

export const theSheetOutcomeIsAFailureOfOurs = (
  said: WhatTheSheetSaid,
): boolean => said === 'would-not-open' || said === 'something-else';

export const whatWentWrong = (
  said: WhatTheSheetSaid,
): WhatWentWrongWithThePayment => {
  switch (said) {
    case 'cancelled':
      return 'abandoned';
    case 'failed':
      // Stripe collected the card and the payment did not go through. The
      // webhook will say why, and will say it better than we can from here.
      return 'refused-by-the-bank';
    case 'would-not-open':
    case 'something-else':
    case 'completed':
      return 'ours-to-fix';
  }
};

/**
 * Whether this deserves to wake somebody.
 *
 * An abandoned basket never does: people change their minds, and an alerting
 * that fires on that is an alerting nobody reads. A refusal never does either,
 * for the same reason, and because the API already counts it with Stripe's own
 * reason attached. What is left is ours.
 */
export const itShouldWakeSomebody = (
  why: WhatWentWrongWithThePayment,
): boolean => why === 'ours-to-fix' || why === 'paid-but-not-confirmed';

/**
 * How Sentry groups these: by the kind, never by the message. The messages
 * carry transaction ids and amounts, so grouping on them would make one issue
 * per payment.
 */
export const howToGroupIt = (
  why: WhatWentWrongWithThePayment,
  stage: string,
): string[] => ['payment', why, stage];
